/**
 * Unit ↔ PropertySection の双方向アダプタ。
 *
 * BE は完全に Unit に統合済みだが、FE 側の PrivateAreaDetail (区画詳細ページ) は
 * 依然として PropertySection 形式の data shape を期待している。propertySectionSlice
 * の thunk が Unit API を呼んだ後、ここで shape 変換して PrivateAreaDetail に渡す。
 *
 * 変換は非可逆部分があるため、書き戻し時はあくまで「Unit に投影できる範囲」のみ送る。
 */

import type { Unit, AreaType, Contract } from "@ippon/shared-types";
import type {
  PropertySection,
  SectionCategoryCode,
  OccupancyStatus,
  RevenueType,
  ContractorDetail,
  SectionContractInfo,
} from "@/types/propertySection";

// ── area_type ↔ section_category マッピング ──

export function areaTypeToSectionCategory(
  areaType: AreaType,
): SectionCategoryCode {
  switch (areaType) {
    case "private":
      return "private_area";
    case "common":
      return "common_area";
    case "facility":
      return "facility";
    case "land":
      return "land";
    case "other":
      return "other";
  }
}

export function sectionCategoryToAreaType(
  category: SectionCategoryCode | undefined,
): AreaType {
  switch (category) {
    case "private_area":
      return "private";
    case "common_area":
      return "common";
    case "facility":
      return "facility";
    case "land":
      return "land";
    default:
      return "other";
  }
}

// ── revenue_type マッピング ──

/** Unit.revenue_type (stock_rent / stock_other) を旧 RevenueType ("rent" / "ancillary_income") に対応付け */
function deriveLegacyRevenueType(unit: Unit): RevenueType | undefined {
  if (unit.revenue_type === "stock_rent") return "rent";
  if (unit.revenue_type === "stock_other") return "ancillary_income";
  return undefined;
}

// ── ContractorDetail に Contracts テーブルのレコードを載せる ──
//
// 契約者・賃料・契約期間の真値は Contracts テーブルのみが保持する。
// Unit.contract / Unit.budget は legacy データで残っているだけで、契約情報の
// ソースとしては使わない (旧データの値を表に流し込むと「契約が無いのに契約者名が
// 出る」現象になる)。
function buildContractorDetailsFromActiveContract(
  contract: Contract,
): ContractorDetail[] {
  const cd: ContractorDetail = {
    contractor_id: contract.contract_id,
    contract_id: contract.contract_id,
    contractor_type: contract.contractor_type,
    update_type: "created",
    monthly_rent: contract.monthly_rent,
    rent_unit_price: contract.rent_unit_price,
    maintenance_fee: contract.maintenance_fee,
    maintenance_fee_unit_price: contract.maintenance_fee_unit_price,
    pricing_type: contract.pricing_type,
    unit_price: contract.unit_price,
    quantity: contract.quantity,
    quantity_unit: contract.quantity_unit,
    security_deposit:
      typeof contract.security_deposit === "number"
        ? String(contract.security_deposit)
        : undefined,
    key_money:
      typeof contract.key_money === "number"
        ? String(contract.key_money)
        : undefined,
    contract_start_date: contract.start_date,
    contract_end_date: contract.end_date,
    contract_form: contract.contract_form,
    contract_remarks: contract.contract_remarks,
    contract_status: contract.status,
    departure_date: contract.departure_date,
    departure_reason: contract.departure_reason,
    created_at: contract.created_at,
    last_updated_at: contract.updated_at,
  };

  if (contract.contractor_type === "individual") {
    const fullName = (contract.contractor_name || "").trim();
    const parts = fullName.split(/[\s\u3000]+/);
    const lastName = parts[0] || "";
    const firstName = parts.slice(1).join(" ");
    cd.individual_details = [
      {
        individual_last_name: lastName,
        individual_first_name: firstName,
        phone_number: contract.phone,
        email: contract.email,
      },
    ];
  } else if (contract.contractor_type === "corporate") {
    cd.corporate_details = [
      {
        company_name: contract.company_name || contract.contractor_name || "",
        company_phone_number: contract.phone,
        company_email: contract.email,
      },
    ];
  }
  return [cd];
}

// ── Unit → PropertySection ──

/**
 * 契約情報 (契約者・賃料・契約期間) は Contracts テーブル (`activeContract`) のみを真値とする。
 * `activeContract` が無い区画は contractor_details / 賃料 / 期間が常に空。
 * Unit.contract / Unit.budget は旧データ互換のための残存項目で、契約情報ソースとしては
 * 利用しない (旧データを表に流し込むと「契約が無いのに契約者名が出る」現象になるため)。
 */
export function unitToPropertySection(
  unit: Unit,
  activeContract?: Contract | null,
): PropertySection {
  const legacyRevenueType = deriveLegacyRevenueType(unit);
  const isRevenue =
    unit.revenue_type === "stock_rent" || unit.revenue_type === "stock_other";

  const contractorDetails = activeContract
    ? buildContractorDetailsFromActiveContract(activeContract)
    : [];

  const sectionContract: SectionContractInfo | undefined = activeContract
    ? {
        contractor_details: contractorDetails,
        individual_details: contractorDetails[0]?.individual_details,
        corporate_details: contractorDetails[0]?.corporate_details,
        monthly_rent: activeContract.monthly_rent,
        maintenance_fee: activeContract.maintenance_fee,
        rent_per_tsubo: activeContract.rent_unit_price,
        maintenance_fee_per_tsubo: activeContract.maintenance_fee_unit_price,
        initial_contract_start_date: activeContract.start_date,
        cancellation_date: activeContract.end_date,
      }
    : undefined;

  // area_type に応じて参照する詳細オブジェクト (専有/共用の「面積/方角」用)
  const spaceLike =
    unit.area_type === "private"
      ? unit.private_detail
      : unit.area_type === "common"
        ? unit.common_detail
        : undefined;

  return {
    property_section_id: unit.unit_id,
    property_id: unit.property_id,
    client_id: unit.client_id,
    section_category: areaTypeToSectionCategory(unit.area_type),
    area_type: unit.area_type,
    section_usage: undefined,
    landmark_name: unit.landmark_name,
    remarks: unit.notes,
    occupancy_status: unit.occupancy_status as OccupancyStatus | undefined,
    is_revenue_generating: isRevenue,
    revenue_type: legacyRevenueType,
    revenue_status: isRevenue ? "active" : undefined,
    contract: sectionContract,
    details: {
      // 区画情報直下フィールドを details にも載せて旧 UI が拾えるように
      floor: spaceLike?.floor ?? unit.floor,
      floor_plan: unit.floor_plan,
      area_sqm: spaceLike?.area_sqm,
      area_tsubo: spaceLike?.area_tsubo,
      ceiling_height_m: spaceLike?.ceiling_height_m,
      direction: spaceLike?.direction,
      // 設備 (facility_detail)
      manufacturer: unit.facility_detail?.manufacturer,
      model_number: unit.facility_detail?.model_number,
      installed_at: unit.facility_detail?.installed_at,
      useful_life_years: unit.facility_detail?.useful_life_years,
      next_replacement_at: unit.facility_detail?.next_replacement_at,
      // 駐車場 (land_detail)
      height_limit_m: unit.land_detail?.height_limit_m,
      width_limit_m: unit.land_detail?.width_limit_m,
    },
    // 区画画像は private_detail.images から取得 (添付書類 documents とは独立フィールド)。
    section_images: unit.private_detail?.images?.map((img) => img.s3_key) ?? [],
    created_at: unit.created_at,
    updated_at: unit.updated_at,
  };
}

export function unitListToPropertySectionList(
  units: Unit[],
  contractsByUnitId?: Record<string, Contract | undefined>,
): PropertySection[] {
  return units.map((u) =>
    unitToPropertySection(u, contractsByUnitId?.[u.unit_id] ?? null),
  );
}

// ── Unit (area_type=facility) → 旧 EquipmentRecord shape ──

/** RentRoll 等の旧 UI が EquipmentRecord 形式を期待する箇所向けの shim */
export function unitToEquipmentRecord(
  unit: Unit,
  activeContract?: Contract | null,
): {
  equipment_id: string;
  property_id: string;
  client_id?: string;
  name: string;
  category?: string;
  location?: string;
  manufacturer?: string;
  model?: string;
  install_date?: string;
  status: string;
  is_revenue_generating?: boolean;
  revenue_type?: "ancillary_income" | "other_income";
  monthly_income?: number;
  contractor_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
} {
  const isRevenue =
    unit.revenue_type === "stock_rent" || unit.revenue_type === "stock_other";
  // 契約者・賃料・期間は Contracts テーブル (activeContract) のみを真値とする。
  // legacy 値 (unit.contract / unit.budget) は契約情報のソースには使わない。
  return {
    equipment_id: unit.unit_id,
    property_id: unit.property_id,
    client_id: unit.client_id,
    name: unit.landmark_name,
    category: undefined,
    manufacturer: unit.facility_detail?.manufacturer,
    model: unit.facility_detail?.model_number,
    install_date: unit.facility_detail?.installed_at,
    status: unit.occupancy_status === "out_of_service" ? "廃止" : "稼働中",
    is_revenue_generating: isRevenue,
    revenue_type: isRevenue ? "ancillary_income" : undefined,
    monthly_income: activeContract?.monthly_rent,
    contractor_name: activeContract?.contractor_name,
    contract_start_date: activeContract?.start_date,
    contract_end_date: activeContract?.end_date,
    notes: unit.notes,
    created_at: unit.created_at,
    updated_at: unit.updated_at,
  };
}

// ── PropertySection 更新ボディ → Unit 更新ボディ ──

/**
 * PrivateAreaDetail が `updatePropertySectionMeta` で送る body を、
 * Unit API の PATCH ボディに変換する。完全な対応ではなく、ページが触るフィールドだけ。
 */
export function metaUpdateBodyToUnitBody(body: {
  landmark_name?: string;
  remarks?: string;
  floor?: string;
  floor_plan?: string;
  rental_area_m2?: number;
  rental_area_tsubo?: number;
  monthly_rent?: number;
  maintenance_fee?: number;
  rent_per_tsubo?: number;
  initial_contract_start_date?: string;
  cancellation_date?: string;
  security_deposit_before?: number;
  key_money?: number;
  section_category?: string;
  section_images?: any;
  [k: string]: unknown;
}): Record<string, unknown> {
  const unitBody: Record<string, unknown> = {};

  if (body.landmark_name !== undefined)
    unitBody.landmark_name = body.landmark_name;
  if (body.remarks !== undefined) unitBody.notes = body.remarks;
  if (body.floor !== undefined) unitBody.floor = body.floor;
  if (body.floor_plan !== undefined) unitBody.floor_plan = body.floor_plan;
  // area_type を直接指定された場合は最優先で採用 (区画詳細フォームの「場所区分」)
  if ((body as any).area_type !== undefined) {
    unitBody.area_type = (body as any).area_type;
  } else if (body.section_category !== undefined) {
    unitBody.area_type = sectionCategoryToAreaType(
      body.section_category as SectionCategoryCode,
    );
  }

  // 旧 UI の revenue_type ("rent" / "ancillary_income" / "other_income" / 空) と
  // is_revenue_generating を、Unit の revenue_type (stock_rent / stock_other / none) に変換。
  // - "rent" → stock_rent (貸室) / "ancillary_income"・"other_income" → stock_other (その他収入)
  // - 区分が読めない場合は area_type で振り分け (専有部=貸室, それ以外=その他収入)
  if (
    (body as any).revenue_type !== undefined ||
    (body as any).is_revenue_generating !== undefined
  ) {
    const legacy = (body as any).revenue_type;
    const flag = (body as any).is_revenue_generating;
    const isStock =
      flag === true ||
      (flag === undefined && typeof legacy === "string" && legacy !== "");
    if (!isStock) {
      unitBody.revenue_type = "none";
    } else if (legacy === "rent") {
      unitBody.revenue_type = "stock_rent";
    } else if (legacy === "ancillary_income" || legacy === "other_income") {
      unitBody.revenue_type = "stock_other";
    } else {
      unitBody.revenue_type =
        unitBody.area_type === "private" ? "stock_rent" : "stock_other";
    }
  }

  // 面積系は private_detail に集約 (PrivateAreaDetail 経由の編集は専有部前提)
  const privateDetail: Record<string, unknown> = {};
  if (body.rental_area_m2 !== undefined)
    privateDetail.area_sqm = body.rental_area_m2;
  if (body.rental_area_tsubo !== undefined)
    privateDetail.area_tsubo = body.rental_area_tsubo;
  if (Object.keys(privateDetail).length > 0)
    unitBody.private_detail = privateDetail;

  // budget サブオブジェクト
  const budget: Record<string, unknown> = {};
  if (body.monthly_rent !== undefined) budget.expected_rent = body.monthly_rent;
  if (body.maintenance_fee !== undefined)
    budget.common_fee = body.maintenance_fee;
  if (body.rent_per_tsubo !== undefined)
    budget.rent_unit_price = body.rent_per_tsubo;
  if (body.initial_contract_start_date !== undefined)
    budget.start_date = body.initial_contract_start_date;
  if (body.cancellation_date !== undefined)
    budget.end_date = body.cancellation_date;
  if (typeof body.security_deposit_before === "number")
    budget.deposit_months = body.security_deposit_before;
  if (typeof body.key_money === "number")
    budget.key_money_months = body.key_money;
  if (Object.keys(budget).length > 0) unitBody.budget = budget;

  return unitBody;
}
